import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import { type UpdateRdoInput, updateRdo } from "@/adapters/api";
import type { Rdo } from "../types.ts";
import { toRdo } from "../utils/map.ts";
import { rdoQueryKey } from "./use-rdo.ts";
import { rdosQueryKey } from "./use-rdos.ts";

export const RDO_AUTOSAVE_DELAY_MS = 800;

function mergePatch(
	current: UpdateRdoInput | null,
	next: UpdateRdoInput,
): UpdateRdoInput {
	if (!current) return { ...next };
	return {
		...current,
		...next,
		signatures:
			current.signatures || next.signatures
				? { ...current.signatures, ...next.signatures }
				: undefined,
	};
}

export function useUpdateRdo(
	constructionId: string | undefined,
	rdoId: string | undefined,
) {
	const queryClient = useQueryClient();
	const pendingRef = useRef<UpdateRdoInput | null>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const mutation = useMutation({
		mutationFn: async (patch: UpdateRdoInput): Promise<Rdo> => {
			if (!constructionId || !rdoId) throw new Error("missing rdo");
			return toRdo(await updateRdo(constructionId, rdoId, patch));
		},
		onSuccess: (rdo) => {
			queryClient.setQueryData(rdoQueryKey(constructionId, rdoId), rdo);
			queryClient.invalidateQueries({ queryKey: rdosQueryKey(constructionId) });
		},
	});

	const { mutateAsync } = mutation;

	const flush = useCallback(async () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		const patch = pendingRef.current;
		pendingRef.current = null;
		if (patch) await mutateAsync(patch).catch(() => undefined);
	}, [mutateAsync]);

	const save = useCallback(
		(patch: UpdateRdoInput) => {
			pendingRef.current = mergePatch(pendingRef.current, patch);
			if (timerRef.current) clearTimeout(timerRef.current);
			timerRef.current = setTimeout(flush, RDO_AUTOSAVE_DELAY_MS);
		},
		[flush],
	);

	const saveNow = useCallback(
		(patch: UpdateRdoInput) => {
			pendingRef.current = mergePatch(pendingRef.current, patch);
			flush();
		},
		[flush],
	);

	useEffect(
		() => () => {
			void flush();
		},
		[flush],
	);

	return {
		save,
		saveNow,
		flush,
		isPending: mutation.isPending,
		isError: mutation.isError,
		error: mutation.error,
	};
}
